import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useFormValidation } from "./useFormValidation";
import { useFileUpload } from "./useFileUpload";
import { useUserNameFetch } from "./useUserNameFetch";
import { addUserFeedback } from "../services/myUserService";

const initialFormState = {
  userName: "",
  mobileNumber: "",
  territory: "",
  category: "",
  subCategory: "",
  Observation: "",
  question1: "",
  question2: "",
};

const territoryAutoCompletePlaceHolder = {
  SH2_Name__c: "",
  SH2_Code__c: "",
};

/**
 * useMyUserForm Hook
 * Manages complete My User form state and submission logic
 * Consolidated hook for better maintainability
 * 
 * @returns {Object} Form state, handlers, and submission logic
 */
export const useMyUserForm = () => {
  const dispatch = useDispatch();
  
  // Form state
  const [userDetails, setUserDetails] = useState(initialFormState);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  
  // Category/Sub-category state
  const [otherFeedback, setOtherFeedback] = useState("");
  const [id, setId] = useState();
  const [queSelect, setQueSelect] = useState("no");
  const [territoryValue, setTerritoryValue] = useState(territoryAutoCompletePlaceHolder);
  
  // Refs
  const formCategoryRef = useRef();
  const formSubCategoryRef = useRef();
  
  // Custom hooks
  const { formValidation, setFormValidation, checkValidation, isValid } = useFormValidation();
  const { file, handleFileRead, removeFile, hasFile } = useFileUpload();
  const {
    userName,
    nameCheck,
    isDisabled: isUserNameDisabled,
    fetchUserName,
    resetUserName,
  } = useUserNameFetch();

  // Memoized change handler
  const onChange = useCallback(async (e, value, type) => {
    const { name, value: targetValue } = e.target || {};

    if (type === "territory") {
      setUserDetails((prev) => ({
        ...prev,
        territory: value || "",
      }));
      return;
    }

    if (name === "mobileNumber") {
      setUserDetails((prev) => ({
        ...prev,
        mobileNumber: targetValue,
        userName: "",
      }));
      resetUserName();
      await fetchUserName(targetValue);
      return;
    }

    if (name === "category") {
      const [categoryId, categoryLabel] = targetValue.split(":");
      setOtherFeedback(categoryLabel);
      setId(categoryId);
      setUserDetails((prev) => ({
        ...prev,
        category: categoryLabel,
      }));
      return;
    }

    if (name === "subCategory") {
      const [, subCategoryLabel] = targetValue.split(":");
      setUserDetails((prev) => ({
        ...prev,
        subCategory: subCategoryLabel,
      }));
      return;
    }

    if (name === "question1") {
      setQueSelect(targetValue);
      setUserDetails((prev) => ({
        ...prev,
        question1: targetValue,
      }));
      return;
    }

    // Default case
    setUserDetails((prev) => ({
      ...prev,
      [name]: targetValue,
    }));
  }, [fetchUserName, resetUserName]);

  // Sync userName from hook to form
  useEffect(() => {
    if (userName) {
      setUserDetails((prev) => ({
        ...prev,
        userName,
      }));
    }
  }, [userName]);

  // Memoized submit handler
  const onSubmit = useCallback((e) => {
    e.preventDefault();
    setIsSubmit(true);
    setFormValidation(checkValidation(userDetails));
  }, [userDetails, checkValidation, setFormValidation]);

  // Handle form submission after validation
  useEffect(() => {
    if (!isSubmit) return;

    (async () => {
      if (isValid) {
        const userDetailsData = {
          RecordType: "My User",
          Observation: userDetails.Observation,
          UserName: userDetails.userName,
          UserMobile: userDetails.mobileNumber,
          Category: userDetails.category,
          SubCategory: otherFeedback === "Any Other Feedback" ? "" : userDetails.subCategory,
          Territory: userDetails.territory,
          filename: file?.fileName || "",
          Question1: userDetails.question1,
          Question2: queSelect === "yes" ? userDetails.question2 : "",
          fileExtension: file?.fileExtension || "",
          base64: file?.base64 || "",
        };

        setLoading(true);
        const response = await addUserFeedback(userDetailsData);
        setLoading(false);

        if (response?.success) {
          // Refresh related Redux data
          const { getFeedbackDetails } = await import("@redux/actions/getFeedback-action");
          const { getMyUserFeedback } = await import("@redux/actions/myuser-actions");
          dispatch(getFeedbackDetails());
          dispatch(getMyUserFeedback());
          
          setIsSubmitModalOpen(true);
          setUserDetails(initialFormState);
          setTerritoryValue(territoryAutoCompletePlaceHolder);
          removeFile();
          resetUserName();
          
          if (formCategoryRef.current) formCategoryRef.current.value = "";
          if (formSubCategoryRef.current) formSubCategoryRef.current.value = "";
          
          setIsSubmit(false);
        } else {
          setIsSubmit(false);
        }
      } else {
        setIsSubmit(false);
      }
    })();
  }, [
    isSubmit,
    isValid,
    userDetails,
    otherFeedback,
    file,
    queSelect,
    dispatch,
    removeFile,
    resetUserName,
  ]);

  return {
    // Form state
    userDetails,
    formValidation,
    loading,
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    
    // Category state
    otherFeedback,
    id,
    queSelect,
    territoryValue,
    setTerritoryValue,
    
    // Refs
    formCategoryRef,
    formSubCategoryRef,
    
    // File state
    file,
    hasFile,
    handleFileRead,
    removeFile,
    
    // User name state
    nameCheck,
    isUserNameDisabled,
    
    // Handlers
    onChange,
    onSubmit,
  };
};
